import { useBack } from '../../navigation/navigation'
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import ConfirmModal from '../../components/ConfirmModal'
import Icon from '../../components/Icon'
import EntityHistory from '../../components/EntityHistory'
import { Button, Select, FormGroup, Input, UnderlineTabs } from '../../components/ui'
import TeamCard from './TeamCard'
import { translateApiError } from '../../utils/errorMessages'
import { DEPARTMENT_TYPE_OPTIONS } from '../../config/departmentTypes'
import { compareTeamsByDepartmentAndName } from '../../utils/sortTeamsByDepartment'
import { formatRub } from '../../utils/formatRub'
import DepartmentDetailHeader from './department-detail/DepartmentDetailHeader'
import DepartmentTeamTabs from './department-detail/DepartmentTeamTabs'
import DepartmentSubTeamTabs from './department-detail/DepartmentSubTeamTabs'
import DepartmentIncomeDashboard from './department-detail/DepartmentIncomeDashboard'
import DepartmentStagesPanel from './department-detail/DepartmentStagesPanel'
import {
  buildStageOverviewRows,
  buildTeamSummaries,
  buildSubTeamTabs,
  filterIncomeTeamBySubTeam,
  findActualTeam,
  findPlannedTeam,
  groupPlannedByTeam,
  resolveSubTeamTabKeyFromUrlParam,
  resolveTeamSummaryFromUrlParam,
  safeAmount,
} from './department-detail/departmentDetailUtils'
import './Company.css'
import './department-detail/DepartmentDetail.css'

function DepartmentCard() {
  const back = useBack('/company/departments')
  const { id } = useParams()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, fetchUser } = useAuth()
  const [department, setDepartment] = useState(null)
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState({ type: '', text: '' })
  const [teams, setTeams] = useState([])
  const [teamsLoading, setTeamsLoading] = useState(false)
  const [allEmployees, setAllEmployees] = useState([])
  const [showTeamForm, setShowTeamForm] = useState(false)
  const [editingTeam, setEditingTeam] = useState(null)
  const [teamFormData, setTeamFormData] = useState({ name: '' })
  const [teamDeleteModal, setTeamDeleteModal] = useState(null)
  const [teamDeleteError, setTeamDeleteError] = useState(null)
  const [showDepartmentEditForm, setShowDepartmentEditForm] = useState(false)
  const [departmentEditFormData, setDepartmentEditFormData] = useState({ name: '', department_type: 'Обслуживание' })
  const [departmentDeleteModal, setDepartmentDeleteModal] = useState(false)
  const [departmentDeleteError, setDepartmentDeleteError] = useState(null)
  const [plannedIncome, setPlannedIncome] = useState(null)
  const [plannedLoading, setPlannedLoading] = useState(false)
  const [plannedError, setPlannedError] = useState(null)
  const [actualIncome, setActualIncome] = useState(null)
  const [stageOverviewIncome, setStageOverviewIncome] = useState(null)
  const [stageTypes, setStageTypes] = useState([])
  
  // URL-based filters (for bookmarks and sharing)
  const urlYear = searchParams.get('year')
  const urlQuarter = searchParams.get('quarter') || 'year'
  const urlSearch = searchParams.get('search') || ''
  const urlSort = searchParams.get('sort') || 'name' // 'name' | 'sum_asc' | 'sum_desc'
  const urlTeamParam = searchParams.get('team')
  const urlSubTeamParam = searchParams.get('subteam')
  const deepLinkRef = useRef({ departmentId: null, team: null, subteam: null })

  if (deepLinkRef.current.departmentId !== id) {
    deepLinkRef.current = {
      departmentId: id,
      team: searchParams.get('team'),
      subteam: searchParams.get('subteam'),
    }
  }
  
  const [incomeYear, setIncomeYear] = useState(() => urlYear ? Number(urlYear) : new Date().getFullYear())
  const [incomeQuarter, setIncomeQuarter] = useState(urlQuarter)
  const [stageSearchInput, setStageSearchInput] = useState(urlSearch)
  const [stageSearch, setStageSearch] = useState(urlSearch)
  const [sortOrder, setSortOrder] = useState(urlSort)
  const [mainTab, setMainTab] = useState('income')
  const [activeTeamKey, setActiveTeamKey] = useState(null)
  const [activeSubTeamKey, setActiveSubTeamKey] = useState('all')
  const [actualLoading, setActualLoading] = useState(false)
  const [actualError, setActualError] = useState(null)

  usePageHeader({
    title: null,
    hideTopHeader: true,
    renderActions: () => null,
  }, [])

  useEffect(() => {
    fetchDepartment()
    fetchTeams()
  }, [id])

  useEffect(() => {
    fetchAllEmployees()
  }, [])

  useEffect(() => {
    if (!id || !department) return
    setPlannedLoading(true)
    setPlannedError(null)
    const params = { year: incomeYear }
    if (stageSearch.trim()) params.stage_search = stageSearch.trim()
    api.get(`/company/departments/${id}/planned-income`, { params })
      .then((res) => {
        setPlannedIncome(res.data?.stages_income ?? [])
      })
      .catch((err) => {
        setPlannedError(translateApiError(err.response?.data?.error || err.message))
        setPlannedIncome(null)
      })
      .finally(() => setPlannedLoading(false))
  }, [id, department, incomeYear, stageSearch])

  useEffect(() => {
    if (!id || !department) return
    setActualLoading(true)
    setActualError(null)
    const params = { year: incomeYear }
    if (incomeQuarter !== 'year') params.quarter = incomeQuarter
    if (stageSearch.trim()) params.stage_search = stageSearch.trim()
    api.get(`/company/departments/${id}/actual-income`, { params })
      .then((res) => {
        setActualIncome({ teams: res.data?.teams ?? [], total: res.data?.total ?? 0 })
      })
      .catch((err) => {
        setActualError(translateApiError(err.response?.data?.error || err.message))
        setActualIncome(null)
      })
      .finally(() => setActualLoading(false))
  }, [id, department, incomeYear, incomeQuarter, stageSearch])

  useEffect(() => {
    if (!id || !department) return
    api.get(`/company/departments/${id}/stage-overview`, { params: {} })
      .then((res) => {
        setStageOverviewIncome(res.data?.stages_income ?? [])
      })
      .catch(() => {
        setStageOverviewIncome([])
      })
  }, [id, department])

  useEffect(() => {
    if (mainTab !== 'stages') return
    api.get('/company/stage-types')
      .then((res) => {
        setStageTypes(res.data?.stage_types ?? [])
      })
      .catch(() => {
        setStageTypes([])
      })
  }, [mainTab])

  // Отдельный эффект для обновления данных пользователя, если нужно
  useEffect(() => {
    // Если пользователь - руководитель, но department_ids не загружены, обновляем данные пользователя
    if (user?.role === 'department_head' && (!user.department_ids || user.department_ids.length === 0)) {
      fetchUser() // Обновляем данные пользователя
    }
  }, [user?.role, fetchUser])

  // Debounce stage search (300ms) to reduce API calls while typing
  useEffect(() => {
    const t = setTimeout(() => setStageSearch(stageSearchInput), 300)
    return () => clearTimeout(t)
  }, [stageSearchInput])

  // Sync filters to URL for bookmarking/sharing (сохраняем deep-link team/subteam)
  useEffect(() => {
    const params = new URLSearchParams()
    if (incomeYear !== new Date().getFullYear()) params.set('year', String(incomeYear))
    if (incomeQuarter !== 'year') params.set('quarter', incomeQuarter)
    if (stageSearch.trim()) params.set('search', stageSearch.trim())
    if (sortOrder !== 'name') params.set('sort', sortOrder)
    const team = searchParams.get('team')
    const subteam = searchParams.get('subteam')
    if (team) params.set('team', team)
    if (subteam) params.set('subteam', subteam)
    setSearchParams(params, { replace: true })
  }, [incomeYear, incomeQuarter, stageSearch, sortOrder, setSearchParams])

  const fetchDepartment = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/company/departments/${id}`)
      if (response.data && response.data.department) {
        setDepartment(response.data.department)
      } else {
        alert('Подразделение не найдено')
        navigate('/company/departments')
      }
    } catch (error) {
      console.error('Error fetching department:', error)
      alert('Ошибка загрузки данных подразделения: ' + translateApiError(error.response?.data?.error || error.message))
      navigate('/company/departments')
    } finally {
      setLoading(false)
    }
  }

  const fetchAllEmployees = async () => {
    try {
      const response = await api.get('/company/employees')
      setAllEmployees(response.data?.employees || [])
    } catch (error) {
      console.error('Error fetching employees for teams:', error)
      setAllEmployees([])
    }
  }

  const fetchTeams = async () => {
    if (!id) return
    try {
      setTeamsLoading(true)
      const listResponse = await api.get('/company/teams', { params: { department_id: id } })
      const baseTeams = listResponse.data?.teams || []
      const detailedTeams = await Promise.all(
        baseTeams.map(async (t) => {
          try {
            const teamResponse = await api.get(`/company/teams/${t.id}`)
            return teamResponse.data?.team || { ...t, members: [] }
          } catch {
            return { ...t, members: [] }
          }
        })
      )
      setTeams(detailedTeams)
    } catch (error) {
      console.error('Error fetching teams:', error)
      setTeams([])
    } finally {
      setTeamsLoading(false)
    }
  }

  const openCreateTeamForm = () => {
    setEditingTeam(null)
    setTeamFormData({ name: '' })
    setShowTeamForm(true)
  }

  const openEditTeamForm = (team) => {
    setEditingTeam(team)
    setTeamFormData({ name: team?.name || '' })
    setShowTeamForm(true)
  }

  const closeTeamForm = () => {
    setShowTeamForm(false)
    setEditingTeam(null)
    setTeamFormData({ name: '' })
  }

  const submitTeamForm = async (event) => {
    event.preventDefault()
    const name = teamFormData.name?.trim()
    if (!name) {
      setMessage({ type: 'error', text: 'Укажите название команды' })
      return
    }
    try {
      if (editingTeam?.id) {
        await api.put(`/company/teams/${editingTeam.id}`, { name, department_id: id })
        setMessage({ type: 'success', text: 'Команда обновлена' })
      } else {
        await api.post('/company/teams', { name, department_id: id })
        setMessage({ type: 'success', text: 'Команда создана' })
      }
      closeTeamForm()
      await fetchTeams()
    } catch (error) {
      setMessage({ type: 'error', text: translateApiError(error.response?.data?.error || error.message) })
    }
  }

  const confirmArchiveTeam = async () => {
    if (!teamDeleteModal?.id) return
    try {
      setTeamDeleteError(null)
      await api.delete(`/company/teams/${teamDeleteModal.id}`)
      setMessage({ type: 'success', text: 'Команда архивирована' })
      setTeamDeleteModal(null)
      setTeamDeleteError(null)
      await fetchTeams()
    } catch (error) {
      setTeamDeleteError(translateApiError(error.response?.data?.error || error.message))
    }
  }

  const openDepartmentEditForm = () => {
    setDepartmentEditFormData({
      name: department?.name || '',
      department_type: department?.department_type || 'Обслуживание',
    })
    setShowDepartmentEditForm(true)
  }

  const closeDepartmentEditForm = () => {
    setShowDepartmentEditForm(false)
    setDepartmentEditFormData({ name: '', department_type: 'Обслуживание' })
  }

  const submitDepartmentEditForm = async (e) => {
    e.preventDefault()
    const name = departmentEditFormData.name?.trim()
    if (!name) {
      setMessage({ type: 'error', text: 'Название подразделения обязательно' })
      return
    }
    try {
      await api.put(`/company/departments/${id}`, {
        name,
        department_type: departmentEditFormData.department_type,
      })
      setMessage({ type: 'success', text: 'Подразделение обновлено' })
      closeDepartmentEditForm()
      await fetchDepartment()
    } catch (err) {
      setMessage({ type: 'error', text: translateApiError(err.response?.data?.error || err.message) })
    }
  }

  const confirmDepartmentDelete = async () => {
    try {
      setDepartmentDeleteError(null)
      await api.delete(`/company/departments/${id}`)
      setDepartmentDeleteModal(false)
      setDepartmentDeleteError(null)
      navigate('/company/departments')
    } catch (err) {
      setDepartmentDeleteError(translateApiError(err.response?.data?.error || err.message))
    }
  }

  const formatDeptMoney = (amount) => {
    const n = amount == null || Number.isNaN(Number(amount)) ? 0 : Number(amount)
    return formatRub(n, { minFrac: 0, maxFrac: 2, showZero: true })
  }

  const plannedByTeamRaw = useMemo(() => groupPlannedByTeam(plannedIncome || []), [plannedIncome])
  const stageOverviewByTeamRaw = useMemo(
    () => groupPlannedByTeam(stageOverviewIncome || []),
    [stageOverviewIncome],
  )

  // Sort teams based on sortOrder
  const sortTeams = useCallback((teams) => {
    if (!teams?.length) return teams
    const sorted = [...teams]
    if (sortOrder === 'sum_desc') {
      sorted.sort((a, b) => (b.total || b.sum || 0) - (a.total || a.sum || 0))
    } else if (sortOrder === 'sum_asc') {
      sorted.sort((a, b) => (a.total || a.sum || 0) - (b.total || b.sum || 0))
    } else {
      sorted.sort((a, b) =>
        compareTeamsByDepartmentAndName(
          { ...a, team_name: a.teamName || a.team_name, name: a.name || a.team_name },
          { ...b, team_name: b.teamName || b.team_name, name: b.name || b.team_name },
        ),
      )
    }
    return sorted
  }, [sortOrder])

  const plannedByTeam = useMemo(() => sortTeams(plannedByTeamRaw), [plannedByTeamRaw, sortTeams])
  const stageOverviewByTeam = useMemo(
    () => sortTeams(stageOverviewByTeamRaw),
    [stageOverviewByTeamRaw, sortTeams],
  )
  const actualTeamsSorted = useMemo(() => sortTeams(actualIncome?.teams || []), [actualIncome?.teams, sortTeams])
  const unassignedEmployees = useMemo(() => {
    const assignedIds = new Set(
      teams.flatMap((team) => (team.members || []).map((member) => member.id))
    )
    return (department?.employees || []).filter((employee) => !assignedIds.has(employee.id))
  }, [department?.employees, teams])

  const teamSummaries = useMemo(() => buildTeamSummaries({
    teams,
    unassignedEmployees,
    plannedTeams: stageOverviewByTeam,
    actualTeams: actualTeamsSorted,
  }), [actualTeamsSorted, stageOverviewByTeam, teams, unassignedEmployees])

  useEffect(() => {
    if (!teamSummaries.length) {
      setActiveTeamKey(null)
      return
    }
    if (deepLinkRef.current.team) return
    if (!teamSummaries.some((team) => team.key === activeTeamKey)) {
      setActiveTeamKey(teamSummaries[0].key)
    }
  }, [activeTeamKey, teamSummaries])

  const activeTeam = useMemo(
    () => teamSummaries.find((team) => team.key === activeTeamKey) || teamSummaries[0] || null,
    [activeTeamKey, teamSummaries],
  )

  const subTeamTabs = useMemo(
    () => buildSubTeamTabs(activeTeam, department?.name),
    [activeTeam, department?.name],
  )

  useEffect(() => {
    if (!teamSummaries.length) return
    const pendingTeam = deepLinkRef.current.team || urlTeamParam
    if (!pendingTeam) return
    const match = resolveTeamSummaryFromUrlParam(teamSummaries, pendingTeam)
    if (match) {
      setActiveTeamKey(match.key)
      deepLinkRef.current.team = null
    }
  }, [teamSummaries, urlTeamParam])

  useEffect(() => {
    const pendingSubTeam = deepLinkRef.current.subteam || urlSubTeamParam
    if (!pendingSubTeam || !subTeamTabs.length) return
    const tabKey = resolveSubTeamTabKeyFromUrlParam(subTeamTabs, pendingSubTeam)
    if (tabKey) {
      setActiveSubTeamKey(tabKey)
      deepLinkRef.current.subteam = null
    }
  }, [subTeamTabs, activeTeamKey, urlSubTeamParam])

  const plannedTeamRaw = useMemo(
    () => findPlannedTeam(plannedByTeam, activeTeam),
    [activeTeam, plannedByTeam],
  )
  const actualTeamRaw = useMemo(
    () => findActualTeam(actualTeamsSorted, activeTeam),
    [activeTeam, actualTeamsSorted],
  )
  const stageOverviewTeamRaw = useMemo(
    () => findPlannedTeam(stageOverviewByTeam, activeTeam),
    [activeTeam, stageOverviewByTeam],
  )

  const plannedTeam = useMemo(
    () => filterIncomeTeamBySubTeam(plannedTeamRaw, activeSubTeamKey),
    [activeSubTeamKey, plannedTeamRaw],
  )
  const actualTeam = useMemo(
    () => filterIncomeTeamBySubTeam(actualTeamRaw, activeSubTeamKey),
    [activeSubTeamKey, actualTeamRaw],
  )
  const stageOverviewTeam = useMemo(
    () => filterIncomeTeamBySubTeam(stageOverviewTeamRaw, activeSubTeamKey),
    [activeSubTeamKey, stageOverviewTeamRaw],
  )
  const stageOverviewRows = useMemo(
    () => buildStageOverviewRows(stageOverviewTeam, null),
    [stageOverviewTeam],
  )

  // Total for the income hero period logic
  const actualTotal = actualIncome?.total || 0

  // Export to CSV
  const handleExportCSV = useCallback(() => {
    const exportTeams = activeSubTeamKey && activeSubTeamKey !== 'all' && actualTeamRaw
      ? [filterIncomeTeamBySubTeam(actualTeamRaw, activeSubTeamKey)].filter((team) => (team?.stages || []).length > 0 || safeAmount(team?.sum) > 0)
      : actualTeamsSorted
    const data = exportTeams
    if (!data.length) return

    const csvNum = (value) => {
      const n = Number(value)
      return Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : ''
    }
    
    const rows = [['Команда', 'Стадия', 'Сотрудник', 'Сумма']]
    
    data.forEach(team => {
      (team.stages || []).forEach(stage => {
        (stage.employees || []).forEach(emp => {
          rows.push([team.team_name, stage.stage_name || '—', emp.employee_name || '—', csvNum(emp.sum)])
        })
        if (!stage.employees?.length) {
          rows.push([team.team_name, stage.stage_name || '—', '—', csvNum(stage.sum)])
        }
      })
      if (!team.stages?.length) {
        rows.push([team.team_name, '—', '—', csvNum(team.sum)])
      }
    })
    
    const csvContent = rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';')).join('\n')
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `доходы_${department?.name || 'подразделение'}_${incomeYear}_факт.csv`
    link.click()
    setTimeout(() => URL.revokeObjectURL(url), 100)
  }, [activeSubTeamKey, actualTeamRaw, actualTeamsSorted, department, incomeYear])

  // Проверяем, может ли пользователь редактировать подразделение
  const canEdit = user?.role === 'admin' || user?.role === 'management' || 
    (user?.role === 'department_head' && user?.department_ids && user.department_ids.includes(id))

  if (loading) {
    return (
      <div className="company-page">
        <div className="loading">Загрузка...</div>
      </div>
    )
  }

  if (!department) {
    return (
      <div className="company-page">
        <p>Подразделение не найдено</p>
        <Button variant="secondary" onClick={back}>
          Назад к списку
        </Button>
      </div>
    )
  }

  const isEditMode = showDepartmentEditForm && canEdit

  return (
    <div className="company-page department-page department-detail-page entity-detail-page">
      <DepartmentDetailHeader
        department={department}
        teamsCount={teamSummaries.length}
        canEdit={canEdit}
        isEditMode={isEditMode}
        onCloseEdit={closeDepartmentEditForm}
        onEdit={openDepartmentEditForm}
        onDelete={() => setDepartmentDeleteModal(true)}
      />

      {message.text && (
        <div className={`message message-${message.type}`} role="alert">
          {message.text}
          <button type="button" className="message-close" onClick={() => setMessage({ type: '', text: '' })} aria-label="Закрыть">×</button>
        </div>
      )}

      {isEditMode && (
        <div className="form-card client-section-card">
          <form id="department-edit-form" onSubmit={submitDepartmentEditForm} className="department-form">
            <h3>Редактирование подразделения</h3>
            <FormGroup label="Название подразделения" required>
              <Input
                type="text"
                value={departmentEditFormData.name}
                onChange={(e) => setDepartmentEditFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </FormGroup>
            <FormGroup label="Тип подразделения" required>
              <Select
                value={departmentEditFormData.department_type}
                onChange={(e) => setDepartmentEditFormData(prev => ({ ...prev, department_type: e.target.value }))}
              >
                {DEPARTMENT_TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </FormGroup>
          </form>
        </div>
      )}

      {isEditMode ? (
        <section className="client-section-card department-teams-section" aria-labelledby="department-teams-heading">
          <div className="client-section-header client-section-header-actions">
            <div className="client-section-header-left">
              <Icon name="users" size={20} aria-hidden />
              <h2 id="department-teams-heading">Команды подразделения</h2>
            </div>
            <Button type="button" variant="primary" onClick={openCreateTeamForm}>
              + Создать команду
            </Button>
          </div>

          {showTeamForm && (
            <div className="form-card">
              <form onSubmit={submitTeamForm} className="department-form">
                <h3>{editingTeam ? 'Редактирование команды' : 'Создание команды'}</h3>
                <FormGroup label="Название команды" required>
                  <Input
                    type="text"
                    value={teamFormData.name}
                    onChange={(e) => setTeamFormData({ name: e.target.value })}
                    required
                  />
                </FormGroup>
                <div className="form-actions">
                  <div className="button-group">
                    <Button type="button" variant="secondary" onClick={closeTeamForm}>
                      Отмена
                    </Button>
                    <Button type="submit" variant="primary">
                      {editingTeam ? 'Сохранить' : 'Создать'}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {teamsLoading ? (
            <p className="info-note">Загрузка команд…</p>
          ) : teams.length === 0 ? (
            <p className="info-note">Команды не созданы</p>
          ) : (
            <div className="org-teams-grid">
              {teams.map((team) => (
                <TeamCard
                  key={team.id}
                  team={team}
                  employees={allEmployees}
                  canEdit={true}
                  onChanged={fetchTeams}
                  onEditTeam={openEditTeamForm}
                  onDeleteTeam={setTeamDeleteModal}
                />
              ))}
            </div>
          )}
        </section>
      ) : (
        <>
          {teamsLoading || teamSummaries.length !== 1 ? (
            <DepartmentTeamTabs
              teams={teamSummaries}
              activeKey={activeTeam?.key}
              loading={teamsLoading}
              onChange={(key) => {
                setActiveTeamKey(key)
                setActiveSubTeamKey('all')
              }}
            />
          ) : null}

          {subTeamTabs.length > 0 ? (
            <DepartmentSubTeamTabs
              tabs={subTeamTabs}
              activeKey={activeSubTeamKey}
              onChange={setActiveSubTeamKey}
            />
          ) : null}

          <UnderlineTabs
            className="department-detail-main-tabs"
            ariaLabel="Разделы подразделения"
            tabs={[
              { id: 'income', label: 'Доходы' },
              {
                id: 'stages',
                label: 'Стадии',
                summary: `${stageOverviewRows.length}`,
              },
            ]}
            activeId={mainTab}
            onChange={setMainTab}
          />

          {mainTab === 'income' ? (
            <section id="income" className="department-income-section" aria-label="Доходы">
              <DepartmentIncomeDashboard
                activeTeam={activeTeam}
                plannedTeam={plannedTeam}
                actualTeam={actualTeam}
                actualTeams={actualTeamsSorted}
                plannedLoading={plannedLoading}
                plannedError={plannedError}
                actualLoading={actualLoading}
                actualError={actualError}
                actualTotal={actualTotal}
                incomeYear={incomeYear}
                setIncomeYear={setIncomeYear}
                incomeQuarter={incomeQuarter}
                setIncomeQuarter={setIncomeQuarter}
                stageSearchInput={stageSearchInput}
                setStageSearchInput={setStageSearchInput}
                sortOrder={sortOrder}
                setSortOrder={setSortOrder}
                handleExportCSV={handleExportCSV}
                formatMoney={formatDeptMoney}
              />
            </section>
          ) : (
            <DepartmentStagesPanel
              activeTeam={activeTeam}
              plannedTeam={plannedTeam}
              actualTeam={actualTeam}
              overviewRows={stageOverviewRows}
              stageTypeOptions={stageTypes}
              formatMoney={formatDeptMoney}
            />
          )}

          <EntityHistory entityType="departments" entityId={id} />
        </>
      )}

      {teamDeleteModal && (
        <ConfirmModal
          title="Удалить команду?"
          message={(
            <p>
              Команда <strong>{teamDeleteModal.name}</strong> будет удалена.
              Исторические распределения доходов не изменятся.
            </p>
          )}
          confirmLabel="Удалить"
          cancelLabel="Отмена"
          confirmVariant="danger"
          onConfirm={confirmArchiveTeam}
          onCancel={() => { setTeamDeleteModal(null); setTeamDeleteError(null); }}
          error={teamDeleteError}
        />
      )}

      {departmentDeleteModal && (
        <ConfirmModal
          title="Удалить подразделение?"
          message={(
            <p>
              Вы уверены, что хотите удалить подразделение <strong>{department?.name}</strong>?
              {department?.employees_count > 0 && (
                <span className="warning-text">
                  {' '}В этом подразделении {department.employees_count} сотрудников.
                </span>
              )}
            </p>
          )}
          confirmLabel="Удалить"
          cancelLabel="Отмена"
          confirmVariant="danger"
          onConfirm={confirmDepartmentDelete}
          onCancel={() => { setDepartmentDeleteModal(false); setDepartmentDeleteError(null); }}
          error={departmentDeleteError}
        />
      )}

    </div>
  )
}

export default DepartmentCard
